import type { ButtonHTMLAttributes} from 'react';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { BigNumber } from 'ethers';
import { useAccount, useSendTransaction } from 'wagmi';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

import {
  useGetOneInchQuoteQuery,
  useGetOneInchTokenAllowanceQuery,
  useIncreaseOneInchTokenAllowanceMutation,
} from '@query/magicSwap';

import { storeSwapTx } from '@api/magicSwap';

import { DEFAULT_QUOTE_REFETCH_INTERVAL } from '@magic-swap/components/pages/MagicSwap/constants';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import { useSwapExperienceStore } from '../utilities/SwapExperienceStore';
import type {
  SingleChainSwapOperation} from '../utilities/SwapTransactionExecutor';
import {
  useSwapOperationExecutor,
} from '../utilities/SwapTransactionExecutor';

const ActionButtonPrototype = ({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      className={classNames(
        'msq-btn msq-btn-lg w-full whitespace-nowrap msq-btn-primary',
        className,
      )}
    >
      {children}
    </button>
  );
};

const SignInActionButton = () => {
  const authModal = useAuthModal();

  const handleSignIn = useCallback(() => {
    authModal.open();
  }, [authModal]);

  return <ActionButtonPrototype onClick={handleSignIn}>Sign in</ActionButtonPrototype>;
};

interface IncreaseAllowanceActionButtonProps {
  disabled?: boolean;
}

const IncreaseAllowanceActionButton = ({ disabled }: IncreaseAllowanceActionButtonProps) => {
  const { source } = useSwapExperienceStore();

  const { address } = useAccount();

  const { mutateAsync: increaseAllowance } = useIncreaseOneInchTokenAllowanceMutation();
  const { refetch: refetchAllowance } = useGetOneInchTokenAllowanceQuery({
    networkId: source.network?.id,
    apiOptions:
      source.token?.id && address
        ? {
            searchParams: {
              tokenAddress: source.token?.id,
              walletAddress: address,
            },
          }
        : undefined,
  });

  const { sendTransactionAsync } = useSendTransaction();

  const handleIncreaseAllowance = async () => {
    if (!source.network || !source.token || !source.amount) {
      return;
    }

    const tx = await increaseAllowance({
      networkId: source.network.id,
      options: {
        searchParams: {
          tokenAddress: source.token.id,
          amount: source.amount.toString(),
        },
      },
    });

    const hash = await sendTransactionAsync({
      ...(tx.data as any),
    });

    if (!hash) {
      return;
    }

    const wagmiChain = wagmiConfig.chains.find(({ id }) => id === source.network?.id);

    if (!wagmiChain) {
      return;
    }

    await handleTransactionResult(hash, wagmiChain.id);

    console.log('Refetching allowance...');

    await refetchAllowance();
  };

  return (
    <ActionButtonPrototype disabled={disabled} onClick={handleIncreaseAllowance}>
      {`Give permission to swap ${source.token?.attributes.symbol}`}
    </ActionButtonPrototype>
  );
};

const PerformSwapActionButton = () => {
  const { exportOperationSpecification } = useSwapExperienceStore();

  const { source, destination, slippagePercent } = exportOperationSpecification();

  const { address } = useAccount();

  const { data: quote } = useGetOneInchQuoteQuery(
    {
      fromNetworkId: source.network?.id,
      toNetworkId: destination.network?.id,
      fromTokenAddress: source.token?.id,
      toTokenAddress: destination.token?.id,
      amount: source.token && source.amount ? source.amount.toString() : undefined,
      walletAddress: address,
    },
    {
      enabled: !!(
        source.network &&
        destination.network &&
        source.token &&
        destination.token &&
        source.amount
      ),
      refetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL,
    },
  );

  const { initiateSwapOperation, markSwapOperationAsCompleted, markSwapOperationAsFailed } =
    useSwapOperationExecutor();

  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const { walletType, userWallet: userWalletInfo } = getUserWalletType();

  const isEnabled = useMemo(() => {
    return (
      source.amount &&
      source.network &&
      source.token &&
      destination.amount &&
      destination.network &&
      destination.token
    );
  }, [source, destination]);

  const handleSwapTokens = useCallback(async () => {
    const toastId = Date.now();

    if (!source.token) {
      console.warn('Source token not selected.');

      return;
    }

    if (!destination.token) {
      console.warn('Destination token not selected.');

      return;
    }

    try {
      if (!quote) {
        return;
      }

      if (source.network.id === destination.network.id) {
        console.log('Initiating a single chain swap operation...');

        const operation = (await Promise.race([
          initiateSwapOperation({ source, destination, slippagePercent }, quote),
          launchAsyncToast({
            walletType,
            address: userWalletInfo.attributes.address,
            toastId,
            showExtraInfo: true,
            hideRetryButton: true,
            title: `Swapping ${source.token.attributes.symbol} to ${destination.token.attributes.symbol}...`,
          }),
        ])) as Awaited<SingleChainSwapOperation>;

        console.log('After initiating sigle-chain swap operation', operation);

        try {
          if (!operation.hash) {
            console.log('Dismissing toast with id:', toastId);

            toast.dismiss(toastId);
          } else {
            console.log('Storing swap transaction...');
            await storeSwapTx({
              networkId: source.network.id,
              options: {
                json: {
                  tx: operation.hash,
                  operationId: operation.id,
                },
              },
            });

            console.log(
              `Swap transaction stored, Marking swap operation with id ${operation.id} as completed...`,
            );

            markSwapOperationAsCompleted(operation.id);

            console.log('Dismissing toast with id:', toastId);
            toast.dismiss(toastId);
          }
        } catch (error) {
          markSwapOperationAsFailed(operation.id, getErrorMessage(error));

          throw error;
        }
      } else {
        console.log('Initiating a cross-chain swap operation...');
        await initiateSwapOperation({ source, destination, slippagePercent }, quote);
      }
    } catch (error) {
      console.error('Error while swapping tokens:', error);

      displayWalletErrorToast({ error, justReturnHandledMessage: true });
    }
  }, [
    quote,
    source,
    destination,
    initiateSwapOperation,
    slippagePercent,
    walletType,
    userWalletInfo.attributes.address,
    markSwapOperationAsCompleted,
    markSwapOperationAsFailed,
  ]);

  return (
    <ActionButtonPrototype disabled={!isEnabled} onClick={handleSwapTokens}>
      Swap
    </ActionButtonPrototype>
  );
};

interface ActionButtonProps {
  disabled?: boolean;
}

const SwapActionButton = ({ disabled }: ActionButtonProps) => {
  const { isAuth } = useAuth();

  const { source, destination } = useSwapExperienceStore();

  const { address } = useAccount();

  const { data: allowance } = useGetOneInchTokenAllowanceQuery({
    networkId: source.network?.id,
    apiOptions:
      source.token?.id && address
        ? {
            searchParams: {
              tokenAddress: source.token?.id,
              walletAddress: address,
            },
          }
        : undefined,
    options: {
      enabled: !!(
        source.network?.id &&
        source.network?.id === destination.network?.id &&
        source.token?.id &&
        address
      ),
    },
  });

  const isEnoughAllowance = useMemo(() => {
    return (
      source.token &&
      BigNumber.from(source.amount ?? 0).lte(BigNumber.from(allowance?.data?.allowance ?? 0))
    );
  }, [source, allowance]);

  if (!isAuth) {
    return <SignInActionButton />;
  }

  if (!isEnoughAllowance) {
    return <IncreaseAllowanceActionButton disabled={disabled} />;
  }

  return <PerformSwapActionButton />;
};

export { SwapActionButton };
